import { createAction, createSlice } from '@reduxjs/toolkit'
// import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectIsTemplate, selectProjectId } from '~/store/app/selectors'
import { selectLive } from '~/store/live/selectors'
import { isDesktop } from '~/store/middleware/pinsSync'
import { restorePins } from '~/store/playback'
import { writePreset } from '~/store/playback/presetWrites'
import { selectPresets } from '~/store/playback/selectors'

// Action Types
export const undo = createAction('history/undo')
export const redo = createAction('history/redo')
export const startBatch = createAction('history/startBatch')
export const endBatch = createAction('history/endBatch')
export const clearHistory = createAction('history/clear')
/** `{ <preset id>: settings }` as they stood before the open step wrote them; the step's first record of a preset wins. */
export const recordPresets = createAction('history/recordPresets')

// An entry is live and the pin layer together, plus the settings of every preset its step wrote.
const snapshot = s => ({ live: selectLive(s), pins: s.playback.pins, pins_project: s.playback.pins_project })

const presetsNow = (s, ids) => {
	if (!ids) return undefined
	const out = {}
	for (const p of selectPresets(s)) if (p?.id in ids) out[p.id] = p.settings
	return out
}

// The layer and presets restore only into the project the entry was taken in.
const restore = (store, entry) => {
	const s = store.getState()
	if (!isDesktop(s) || entry.pins_project !== s.playback.pins_project) return
	if (entry.pins && entry.pins !== s.playback.pins) store.dispatch(restorePins(entry.pins))
	if (!entry.presets || selectIsTemplate(s)) return
	const projectId = selectProjectId(s)
	for (const p of selectPresets(s)) {
		const settings = entry.presets[p?.id]
		if (settings && settings !== p.settings) writePreset(store.dispatch, { projectId, id: p.id, settings })
	}
}

const initialState = {
	past: [],
	future: [],
	batch: {
		isActive: false,
		actions: [],
		savedState: null,
	},
}

// Selectors
export const selectHistory = s => s.history

// Custom hook for history state
export const useHistory = () => {
	const history = useSelector(selectHistory)
	const canUndo = history.past.length > 0
	const canRedo = history.future.length > 0

	return {
		canUndo,
		canRedo,
		past: history.past.length,
		future: history.future.length,
		isInBatch: history.batch.isActive,
	}
}

// Configuration
const MAX_HISTORY_LENGTH = 50
const TRACKED_ACTIONS = new Set([
	'live/setLiveProperty',
	'live/setObjects',
	'live/addObjects',
	'live/updateObject',
	'live/updateObjects',
	'live/deleteObjects',
	'live/groupObjects',
	'live/unGroupObjects',
	'live/sortObjects',
	'live/setObjectMask',
	'live/setSourceValues',
	'live/applySourceFill',
	'live/setModulator',
	'live/updateModulator',
	'live/linkModulator',
])

const historySlice = createSlice({
	name: 'history',
	initialState,
	reducers: {
		pushPast(state, action) {
			state.past = [...state.past, action.payload].slice(-MAX_HISTORY_LENGTH)
			state.future = []
		},
		pushPastFromRedo(state, action) {
			state.past = [...state.past, action.payload].slice(-MAX_HISTORY_LENGTH)
		},
		popPast(state) {
			state.past = state.past.slice(0, -1)
		},
		pushFuture(state, action) {
			state.future = [action.payload, ...state.future]
		},
		popFuture(state) {
			state.future = state.future.slice(1)
		},
		startBatching(state, action) {
			state.batch.isActive = true
			state.batch.actions = []
			state.batch.savedState = action.payload
		},
		endBatching(state) {
			state.batch.isActive = false
			state.batch.actions = []
			state.batch.savedState = null
		},
		addToBatch(state, action) {
			state.batch.actions.push(action.payload)
		},
		addPresets(state, { payload }) {
			const entry = state.batch.isActive ? state.batch.savedState : state.past[state.past.length - 1]
			if (!entry) return
			entry.presets = { ...payload, ...entry.presets }
		},
		reset(state) {
			state.past = []
			state.future = []
			state.batch.isActive = false
			state.batch.actions = []
			state.batch.savedState = null
		},
	},
})

const {
	pushPast,
	pushPastFromRedo,
	popPast,
	pushFuture,
	popFuture,
	startBatching,
	endBatching,
	addToBatch,
	addPresets,
	reset,
} = historySlice.actions

export const historyReducer = historySlice.reducer

const historyMiddleware = store => next => action => {
	// Handle history actions
	if (action.type === undo.type) {
		const state = store.getState()
		const { past } = state.history

		if (past.length === 0) return

		const previousState = past[past.length - 1]
		const currentState = { ...snapshot(state), presets: presetsNow(state, previousState.presets) }

		store.dispatch(pushFuture(currentState))
		store.dispatch(popPast())
		restore(store, previousState)

		return next({
			type: 'live/setLiveObject',
			payload: previousState.live,
			meta: { skipHistory: true },
		})
	}

	if (action.type === redo.type) {
		const state = store.getState()
		const { future } = state.history

		if (future.length === 0) return

		const nextState = future[0]
		const currentState = { ...snapshot(state), presets: presetsNow(state, nextState.presets) }

		store.dispatch(pushPastFromRedo(currentState))
		store.dispatch(popFuture())
		restore(store, nextState)

		return next({
			type: 'live/setLiveObject',
			payload: nextState.live,
			meta: { skipHistory: true },
		})
	}

	if (action.type === recordPresets.type) {
		store.dispatch(addPresets(action.payload))
		return
	}

	if (action.type === startBatch.type) {
		// Save the state BEFORE the batch starts so undo restores pre-batch state
		store.dispatch(startBatching(snapshot(store.getState())))
		return
	}

	if (action.type === endBatch.type) {
		const state = store.getState()
		if (state.history.batch.actions.length > 0) {
			store.dispatch(pushPast(state.history.batch.savedState))
		}
		store.dispatch(endBatching())
		return
	}

	if (action.type === clearHistory.type) {
		store.dispatch(reset())
		return
	}

	// Skip if action shouldn't be tracked; a map-derived write is a function of the map, so an undo re-derives it rather than restoring it.
	const shouldTrack = TRACKED_ACTIONS.has(action.type) && !action.meta?.skipHistory && !action.meta?.mapDerived

	if (shouldTrack) {
		// Store the current state BEFORE applying the action
		const currentState = snapshot(store.getState())
		const { batch } = store.getState().history

		if (!batch.isActive) {
			store.dispatch(pushPast(currentState))
		} else {
			store.dispatch(addToBatch(action))
		}
	}

	// Execute the action
	const result = next(action)

	return result
}

// Thunk to push the current state to history

export const pushHistory = (dispatch, getState) => {
	// Store the current state BEFORE applying the action
	const s = getState()
	const currentState = snapshot(s)
	const { batch } = s.history

	if (!batch.isActive) {
		dispatch(pushPast(currentState))
	} else {
		throw new Error('Cannot push history while in batch mode')
	}
}

export default historyMiddleware
